'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'motion/react';
import { InView } from '@/components/motion/InView';
import { CircleDot } from 'lucide-react';
import { localized, type HomeContentConfig, type RestaurantLocale } from '@restaurant/config';
import type { CurrentOpeningStatus, HoursMessageSettings, OpeningHourSchedule } from '@restaurant/db';
import styles from './HoursSection.module.css';

const EASE = [0.22, 1, 0.36, 1] as const;

interface HoursSectionProps {
  content: HomeContentConfig['hours'];
  locale: RestaurantLocale;
  messages?: HoursMessageSettings;
  openingHours?: OpeningHourSchedule[];
  openingStatus?: CurrentOpeningStatus;
}

export function HoursSection({ content, locale, messages, openingHours = [], openingStatus }: HoursSectionProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const statusTitle = openingStatus?.label || localized(content.openBadge, locale);
  const statusText =
    (locale === 'fr' ? messages?.homeMessageFr : null) ||
    openingStatus?.detail ||
    localized(openingStatus?.isOpen === false ? content.closedDescription : content.openDescription, locale);

  return (
    <section className={styles.section} id="horaires">
      <div className={styles.container}>
        {/* Schedule card — slides from left */}
        <InView x={-24} y={0} delay={0} duration={0.8}>
          <div className={styles.scheduleCard}>
            <h2 className={styles.title}>{localized(content.title, locale)}</h2>
            <div className={styles.schedule}>
              {openingHours.map((day, i) => (
                mounted && !reduced ? (
                  <motion.div
                    key={day.dayOfWeek}
                    className={styles.row}
                    initial={{ opacity: 0, x: -14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.055, ease: EASE }}
                  >
                    <span className={styles.day}>{day.dayLabel}</span>
                    <span className={styles.time}>{day.displayLabel}</span>
                  </motion.div>
                ) : (
                  <div key={day.dayOfWeek} className={styles.row}>
                    <span className={styles.day}>{day.dayLabel}</span>
                    <span className={styles.time}>{day.displayLabel}</span>
                  </div>
                )
              ))}
            </div>
          </div>
        </InView>

        {/* Status panel — slides from right */}
        <InView x={24} y={0} delay={0.15} duration={0.8}>
          <div className={styles.statusPanel}>
            <Image
              className={styles.statusImage}
              src={content.imageUrl}
              alt={localized(content.imageAlt, locale)}
              fill
              quality={70}
              sizes="(max-width: 980px) 100vw, 50vw"
            />
            <div className={styles.statusOverlay}>
              <div className={styles.statusCard}>
                <CircleDot className={`${styles.statusIcon} ${openingStatus?.isOpen === false ? styles.statusClosed : ''}`} aria-hidden="true" />
                <p className={styles.statusEyebrow}>Live</p>
                <h3 className={styles.statusTitle}>{statusTitle}</h3>
                <p className={styles.statusText}>{statusText}</p>
              </div>
            </div>
          </div>
        </InView>
      </div>
    </section>
  );
}
